// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2025-04-29 22:05:21 +0200 (Tue, 29 Apr 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 152034 $
//  Purpose   : showcase components
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'

// ---Framework imports
import log from 'uix/logging'
import {useApi, useSelector, useState, useCallback, useRef, useEffect, useInView} from 'uix/hooks'
import {Sel} from 'uix/registries'
import {AccessWarning, GhostImage, GhostText, PageViewer, Modal, Button} from '.'
import {VIcon} from 'graphics'

// ---Local imports
import S from './showcase.scss'
import {useCaptionLink} from '../hooks'

/// ---Exports
const useImageInfo = id => {
  let w = 504
  let imgsrc = `/download/webcache/${w}/${id}`
  let [isError, setIsError] = useState(false)

  let onError = useCallback(() => setIsError(true), [])

  return {imgsrc, onError, isError}
}

export const Showcase = ({
  otid,
  otids,
  caption,
  fontSize,
  linkid,
  carousel,
  imagePosition,
  imageWidth = '504px',
  limitText,
}) => {
  let apiurl = useSelector(Sel.apiurl)
  let [outerRef, inView] = useInView({triggerOnce: true, rootMargin: '100%'})
  let imgRef = useRef(null)
  let contentRef = useRef(null)
  let [hidden, setHidden] = useState(false)
  let [txtOverflow, setTxtOverflow] = useState(false)
  let [imgHeight, setImgHeight] = useState(false)
  let [imgLoad, setImgLoad] = useState(false)
  let [currentIndex, setCurrentIndex] = useState(0) // Neuer Zustand für Carousel
  let [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    let imgHeight = imgRef.current?.height || 0
    let contentHeight = contentRef.current?.clientHeight || 0
    setHidden(false)
    setImgHeight(imgHeight)
    setTxtOverflow(imgHeight < contentHeight - 5)
  }, [imgLoad, currentIndex])

  const flattenPages = (node, parentText = '', parentTitle = '') => {
    let pages =
      node.pages?.map(p => ({
        ...p,
        text: p.text || node.text || parentText,
        title: parentTitle || node.title || parentTitle, // Parent-Title speichern
      })) || []

    node.children?.forEach(child => {
      pages = pages.concat(flattenPages(child, node.text, parentTitle))
    })

    return pages
  }

  let visible = !!otid && inView
  let {data, loading} = useApi(
    'presentation/getShowcaseData',
    {otid, resolveChildren: !!carousel},
    {condition: visible}
  )

  let allPages = carousel
    ? data
      ? flattenPages(data)
      : []
    : data?.pages?.[0]
    ? [{...data.pages[0], text: data.text, awstate: data.awstate}]
    : []

  let totalSlides = allPages.length
  let {imgsrc, onError} = useImageInfo(allPages[currentIndex]?.otid)

  let onGraphicClick = () => setModalOpen(true)
  let nextSlide = () => setCurrentIndex(prev => (prev + 1) % totalSlides)
  let prevSlide = () => setCurrentIndex(prev => (prev - 1 + totalSlides) % totalSlides)

  let img = (
    <AccessWarning id={currentIndex} awstate={allPages[currentIndex]?.awstate}>
      <img
        onLoad={() => setImgLoad(true)}
        onClick={onGraphicClick}
        src={apiurl.href(imgsrc)}
        onError={onError}
        ref={imgRef}
        loading="lazy"
      />
    </AccessWarning>
  )

  let textStyle = null
  if (!hidden && !loading) {
    textStyle = {
      maxHeight: !limitText ? 'none' : `${imgHeight - 5}px`,
    }
  }

  let buildMore = () => {
    if (!limitText) return null
    let handleMore = () => setHidden(!hidden)
    if (!hidden) {
      return (
        <a onClick={handleMore} className={S.more}>
          mehr <VIcon size="small" name="arrow down" />
        </a>
      )
    }
  }
  let modalContent = (
    <PageViewer
      mobile={false}
      optionSet="grid"
      // otids={allPages.map(pages => pages.otid)}
      otids={[allPages[currentIndex]?.otid]}
      height="100%"
      zoomControls="inOut"
    />
  )
  let captionLink = useCaptionLink(caption, linkid, otid, null, true)
  return (
    <div className={clsx(S.showcase, imagePosition === 'right' && S.imageRight)} ref={outerRef}>
      <div className={clsx(S.content, loading && S.loading)}>
        {carousel && totalSlides > 1 && (
          <div className={S.carouselNav}>
            <a onClick={currentIndex > 0 ? prevSlide : undefined}>
              <VIcon className={clsx(S.prev, currentIndex <= 0 && S.disabled)} name="arrow left" />
            </a>
            <span>
              {currentIndex + 1} / {totalSlides}
            </span>
            <a onClick={currentIndex < totalSlides - 1 ? nextSlide : undefined}>
              <VIcon
                className={clsx(S.prev, currentIndex >= totalSlides - 1 && S.disabled)}
                name="arrow right"
              />
            </a>
          </div>
        )}
        <div className={S.imgContainer} style={{'--presenation_showcase_imageWidth': imageWidth}}>
          {loading ? (
            <GhostImage width="100%" height={imgHeight || '320px'} />
          ) : (
            <>
              <div className={S.clickable}></div>
              <Button
                size="large"
                style={{
                  color: 'var(--presentation_themeColor)',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                }}
                onClick={onGraphicClick}
                className={S.zoomButton}
                icon={'zoom'}
                circle
                inverted
              />
              {img}
              {captionLink}
            </>
          )}
        </div>
        {loading ? (
          <GhostText style={{marginLeft: '20px', width: 'inherit'}} />
        ) : (
          <div
            style={textStyle}
            className={clsx(S.textArea, limitText && txtOverflow && !hidden && S.hidden)}
          >
            <div key={currentIndex} ref={contentRef} style={{fontSize: fontSize}}>
              {/* {allPages[currentIndex]?.title}*/}
              {allPages[currentIndex]?.text}
            </div>
          </div>
        )}
        {!loading && <div className={S.actions}>{txtOverflow && buildMore()}</div>}
      </div>
      {modalOpen && (
        <Modal
          mobile={false}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          imageId={allPages[currentIndex]?.otid}
        >
          {modalContent}
        </Modal>
      )}
    </div>
  )
}
